import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask-next';
import { Plus, Trash2, Users, Info, CircleAlert as AlertCircle, TriangleAlert as AlertTriangle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { Contact } from './types';
import { emptyContact } from './types';

interface ContactMatch {
  phone: string;
  client_name: string;
  contact_name: string;
  unit_name: string;
}

interface Props {
  contacts: Contact[];
  clientPhone: string;
  clientCpf: string;
  contractTempId: string;
  prefilled?: boolean;
  onComplete: (contacts: Contact[]) => void;
}

const MIN_CONTACTS = 1;

export default function ContactsStep({ contacts: initialContacts, clientPhone, clientCpf, prefilled, onComplete }: Props) {
  const [contacts, setContacts] = useState<Contact[]>(
    initialContacts.length >= MIN_CONTACTS ? initialContacts : [emptyContact()]
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [matches, setMatches] = useState<Record<string, ContactMatch[]>>({});
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState(false);

  const phonesKey = contacts
    .map(c => c.telefone.replace(/\D/g, ''))
    .filter(d => d.length >= 10)
    .sort()
    .join(',');

  useEffect(() => {
    const phones = phonesKey ? [...new Set(phonesKey.split(','))] : [];
    if (phones.length === 0) {
      setMatches({});
      setCheckError(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setChecking(true);
      const { data, error } = await supabase.rpc('find_additional_contact_matches', {
        p_phones: phones,
        p_exclude_cpf: clientCpf,
      });
      if (cancelled) return;
      setChecking(false);
      if (error || !Array.isArray(data)) {
        setCheckError(true);
        return;
      }
      setCheckError(false);
      const grouped: Record<string, ContactMatch[]> = {};
      (data as ContactMatch[]).forEach(m => {
        (grouped[m.phone] ||= []).push(m);
      });
      setMatches(grouped);
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phonesKey, clientCpf]);

  const updateContact = (index: number, field: keyof Contact, value: string) =>
    setContacts(prev => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));

  const validate = () => {
    const errs: Record<string, string> = {};
    const phones = new Set<string>();
    const clientDigits = clientPhone.replace(/\D/g, '');

    contacts.forEach((c, i) => {
      const digits = c.telefone.replace(/\D/g, '');
      if (digits.length < 10) errs[`tel-${i}`] = 'Telefone inválido';
      else if (phones.has(digits)) errs[`tel-${i}`] = 'Telefone duplicado';
      else if (digits === clientDigits) errs[`tel-${i}`] = 'Não pode ser o telefone do cliente';
      else phones.add(digits);
      if (!c.relacao.trim()) errs[`rel-${i}`] = 'Grau de parentesco obrigatório';
    });

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Contatos Adicionais</h2>
        <p className="text-gray-600 text-sm">
          Cadastre pelo menos <strong>1 contato</strong> de referência do cliente. Você pode adicionar quantos quiser.
        </p>
      </div>

      {prefilled && (
        <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
          <span>Contatos carregados do contrato anterior — verifique e atualize se necessário.</span>
        </div>
      )}

      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-500">
          {contacts.length} contato{contacts.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => setContacts(prev => [...prev, emptyContact()])}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <Plus className="w-4 h-4" /> Adicionar contato
        </button>
      </div>

      <div className="space-y-4">
        {contacts.map((contact, i) => (
          <div
            key={i}
            className={`bg-white border-2 rounded-xl overflow-hidden ${
              Object.keys(errors).some(k => k.endsWith(`-${i}`)) ? 'border-red-200' : 'border-gray-100'
            }`}
          >
            <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-semibold text-gray-700">
                  Contato {i + 1}
                  {i === 0 && <span className="ml-1.5 text-xs font-normal text-gray-400">(obrigatório)</span>}
                </span>
              </div>
              {contacts.length > MIN_CONTACTS && (
                <button
                  onClick={() => setContacts(prev => prev.filter((_, idx) => idx !== i))}
                  className="text-red-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-4 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                <InputMask
                  mask="(99) 99999-9999"
                  value={contact.telefone}
                  onChange={(e) => updateContact(i, 'telefone', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl text-sm transition-colors ${
                    errors[`tel-${i}`] ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="(00) 00000-0000"
                />
                {errors[`tel-${i}`] && (
                  <p className="text-red-500 text-xs mt-1">{errors[`tel-${i}`]}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Grau de parentesco *</label>
                <input
                  type="text"
                  value={contact.relacao}
                  onChange={(e) => updateContact(i, 'relacao', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-xl text-sm transition-colors ${
                    errors[`rel-${i}`] ? 'border-red-400 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Mãe, Irmão..."
                />
                {errors[`rel-${i}`] && (
                  <p className="text-red-500 text-xs mt-1">{errors[`rel-${i}`]}</p>
                )}
              </div>
            </div>

            {(matches[contact.telefone.replace(/\D/g, '')] || []).length > 0 && (
              <div className="mx-4 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold mb-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 text-amber-600" />
                  Este telefone já é contato adicional de outro cliente
                </div>
                <div className="space-y-1.5">
                  {matches[contact.telefone.replace(/\D/g, '')].map((m, idx) => (
                    <div key={idx} className="text-xs text-amber-900 bg-white/70 rounded-lg px-3 py-2 border border-amber-100 leading-relaxed">
                      <div><span className="text-amber-700">Cliente:</span> <span className="font-semibold">{m.client_name}</span></div>
                      <div><span className="text-amber-700">Cadastrado como:</span> <span className="font-semibold">{m.contact_name || '-'}</span></div>
                      <div><span className="text-amber-700">Unidade:</span> <span className="font-semibold">{m.unit_name || '-'}</span></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {checking && (
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Verificando contatos em outros contratos...
        </div>
      )}
      {checkError && !checking && (
        <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 text-xs">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Não foi possível verificar se os contatos já estão cadastrados em outros contratos.
        </div>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          Corrija os erros acima para continuar.
        </div>
      )}

      <button
        onClick={() => { if (validate()) onComplete(contacts); }}
        className="w-full py-4 bg-blue-600 text-white rounded-xl font-semibold text-base hover:bg-blue-700 transition-colors shadow-sm"
      >
        Continuar
      </button>
    </div>
  );
}
